import type { ReactNode } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { Badge } from '@bliks-io/ui';
import { WizardCallout } from './WizardCallout';

interface WizardStepHeaderProps {
  title: string;
  description?: string;
  hint?: string;
  /** Shows the done badge next to the title and the done notice below */
  isDone?: boolean;
  /** Badge text while done, e.g. „Abgeschlossen" */
  doneBadge?: string;
  /** The callout under the header while done */
  doneNotice?: { title: string; description: ReactNode };
  /** Rendered top-right: a clear button, an upload button, … */
  action?: ReactNode;
}

/**
 * The one header of every wizard step: title (+ done badge), description and
 * optional hint on the left, the step's action top-right, the done callout
 * underneath. Keeps the steps of a wizard stylistically identical.
 */
export function WizardStepHeader({
  title,
  description,
  hint,
  isDone = false,
  doneBadge,
  doneNotice,
  action
}: WizardStepHeaderProps) {
  return (
    <>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="mb-2.5 font-display text-[30px] font-bold tracking-[-0.02em] text-foreground">
              {title}
            </h2>
            {isDone && doneBadge && (
              <Badge colorScheme="success" className="mb-2.5 gap-1">
                <CheckCircle2 className="h-3.5 w-3.5" />
                {doneBadge}
              </Badge>
            )}
          </div>
          {description && <p className="max-w-[88ch] text-sm text-fg-secondary">{description}</p>}
          {hint && <p className="mt-2 text-[13px] italic text-fg-subtle">{hint}</p>}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>

      {isDone && doneNotice && (
        <WizardCallout variant="success" title={doneNotice.title}>
          {doneNotice.description}
        </WizardCallout>
      )}
    </>
  );
}
